import { z } from "zod";
import { ActorType } from "@common/types/auditLog";
import { omit } from "@common/helpers/objects";
import { FinancialDocumentFilters } from "@components/documents/FinancialDocumentsPageFilters/lib";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "../useWorkspaceUserCache";

const financialDocumentFiltersCacheSchema = z.object({
    start: z.coerce.date().optional(),
    end: z.coerce.date().optional(),
    isImported: z.boolean().optional(),
    isImporting: z.boolean().optional(),
    isNotImported: z.boolean().optional(),
    isDocReconciled: z.boolean().optional(),
    isDocReconciling: z.boolean().optional(),
    isDocNotReconciled: z.boolean().optional(),
    isDocMatched: z.boolean().optional(),
    isDocNotMatched: z.boolean().optional(),
    entityIds: z.array(z.number()).optional(),
    hasNoEntity: z.boolean().optional(),
    periodStart: z.string().optional(),
    periodEnd: z.string().optional(),
    documentCategoryIds: z.array(z.string()).optional(),
    uploaderIds: z.array(z.union([z.literal(null), z.number()])).optional(),
    uploaderType: z.nativeEnum(ActorType).optional(),
    uploaderSources: z.array(z.string()).optional(),
    accountIds: z.array(z.number()).optional(),
    hasNoFinancialAccount: z.boolean().optional(),
    counterpartiesIds: z.array(z.string()).optional(),
    hasNoCounterparty: z.boolean().optional(),
    withEmptyOrUnverifiedCounterparty: z.boolean().optional(),
    fileExtensions: z.array(z.string()).optional(),
});

const sanitizeFilters = (
    filters: FinancialDocumentFilters | undefined,
): FinancialDocumentFilters | undefined => {
    if (!filters) {
        return undefined;
    }

    return omit(filters, ["search"]);
};

export function useFinancialDocumentsPageFiltersCache(): [
    FinancialDocumentFilters | undefined,
    (value: FinancialDocumentFilters | undefined) => void,
] {
    const [value, setter] = useWorkspaceUserCache<FinancialDocumentFilters>(
        WORKSPACE_USER_CACHE_KEYS.FINANCIAL_DOCUMENTS_PAGE_FILTERS_CACHE_KEY,
        financialDocumentFiltersCacheSchema,
    );

    return [
        value,
        (filters: FinancialDocumentFilters | undefined) => {
            setter(sanitizeFilters(filters));
        },
    ];
}
